import type { InfiniteData, QueryClient } from '@tanstack/vue-query'
import { getChatQueryKey, PERSONAL_CHATS_QUERY_KEY, PUBLIC_CHATS_QUERY_KEY, type Chat } from '#entities/chat'
import type { GetChatsResponse } from '#entities/chat/model/get-chats-response'
import type { Message } from '../model/message'
import { areMessagesEqual } from './are-messages-equal'
import { getFreshMessagesQueryKey } from './fresh-messages-query'

export function appendMessage(queryClient: QueryClient, message: Message) {
  queryClient.setQueryData(getFreshMessagesQueryKey(message.chatId), (queryData: Message[] | undefined) => {
    if (!queryData) return [message]

    const doesMessageExists = queryData.some(existingMessage => areMessagesEqual(existingMessage, message))
    if (doesMessageExists) return queryData

    return [...queryData, message]
  })

  queryClient.setQueryData(PUBLIC_CHATS_QUERY_KEY, (queryData: GetChatsResponse | undefined) => {
    if (!queryData) return

    const newChats = queryData.data.map(chat => {
      if (chat.id !== message.chatId) return chat

      return {
        ...chat,
        lastMessage: message,
      }
    })

    return {
      ...queryData,
      data: newChats,
    }
  })

  queryClient.setQueryData(PERSONAL_CHATS_QUERY_KEY, (queryData: InfiniteData<GetChatsResponse> | undefined) => {
    if (!queryData) return

    const newPages = queryData.pages.map(page => ({
      ...page,
      data: page.data.map(chat => {
        if (chat.id !== message.chatId) return chat

        return {
          ...chat,
          lastMessage: message,
        }
      }),
    }))

    return {
      ...queryData,
      pages: newPages,
    }
  })

  // Invalidating personal chats because of order change to avoid broken cursors
  queryClient.invalidateQueries({ queryKey: PERSONAL_CHATS_QUERY_KEY, exact: true })

  queryClient.setQueryData(getChatQueryKey(message.chatId), (queryData: Chat | undefined) => {
    if (!queryData) return

    return {
      ...queryData,
      lastMessage: message,
    }
  })
}
